import { IRoleSymbols } from 'app/models/permission.model';
import { RecipientType } from 'app/models/alarmroutes.model';
import { UserRoleLevel } from 'app/models/user.model';

/**
 * Role symbols: the icon and colour that show a staff member's role. Staff Management, the role legend
 * and the alarm route recipients use these, so a role looks the same everywhere (ES-7564).
 *
 * Built-in roles have a fixed symbol: the user icon in the role's colour (solid for customer admins).
 * Custom roles have the icon chosen in the role editor, in the default colour (custom roles have no
 * colour setting).
 */

/** Icon of the built-in roles and of custom roles without an icon */
export const DEFAULT_ROLE_ICON = 'fa-user';

/** Colour class of each built-in role, defined in css/styles.css. Read-only users have the default colour. */
const BUILT_IN_ROLE_COLORS: Readonly<Record<number, string>> = {
  [UserRoleLevel.CustomerAdmin]: 'role-color-customer-admin',
  [UserRoleLevel.MainUser]: 'role-color-main-user',
  [UserRoleLevel.Editor]: 'role-color-user',
  [UserRoleLevel.AnnaPerennaMainUser]: 'role-color-onon-main-user',
  [UserRoleLevel.EMAOnly]: 'role-color-ema',
  [UserRoleLevel.EmaacOperator]: 'role-color-emaac-operator',
  [UserRoleLevel.ControlRoomUser]: 'role-color-emaac-account',
};

export interface RoleSymbol {
  /** Font Awesome icon class, e.g. "fa-user" */
  icon: string;
  /** Colour class, empty for the default colour */
  colorClass: string;
  /** Solid instead of light icon style */
  solid: boolean;
}

/** No custom roles: every staff member shows the built-in role of their level */
export const NO_ROLE_SYMBOLS: IRoleSymbols = { roles: [], staffRoles: {} };

/** Symbol of the built-in role of a level */
export function builtInRoleSymbol(level: number | null | undefined): RoleSymbol {
  return {
    icon: DEFAULT_ROLE_ICON,
    colorClass: BUILT_IN_ROLE_COLORS[level] ?? '',
    solid: level === UserRoleLevel.CustomerAdmin,
  };
}

/** Symbol of a custom role: its icon in the default colour */
export function customRoleSymbol(icon: string | null | undefined): RoleSymbol {
  return { icon: icon || DEFAULT_ROLE_ICON, colorClass: '', solid: false };
}

/** Symbol of a staff member's role: the custom role they have, otherwise the built-in role of their level */
export function staffRoleSymbol(staffId: string, level: number | null | undefined, symbols: IRoleSymbols | null | undefined): RoleSymbol {
  const roleId = symbols?.staffRoles?.[staffId];
  const role = roleId != null ? symbols.roles?.find(r => r.id === roleId) : undefined;
  return role ? customRoleSymbol(role.icon) : builtInRoleSymbol(level);
}

/**
 * Key of a staff member's role, as the legend entries have it: "custom:<id>" for a custom role holder,
 * otherwise "builtIn:<level>". Used to filter staff by role.
 */
export function staffRoleKey(staffId: string, level: number | null | undefined, symbols: IRoleSymbols | null | undefined): string {
  const roleId = symbols?.staffRoles?.[staffId];
  if (roleId != null && symbols.roles?.some(r => r.id === roleId)) {
    return `custom:${roleId}`;
  }
  return `builtIn:${level ?? 0}`;
}

/**
 * The symbols with one staff member's custom role set (or removed with null), as a new object so that
 * caches keyed on the symbols object see the change
 */
export function withStaffRole(symbols: IRoleSymbols | null | undefined, staffId: string, roleId: number | null): IRoleSymbols {
  const staffRoles = { ...(symbols?.staffRoles ?? {}) };
  if (roleId == null) {
    delete staffRoles[staffId];
  } else {
    staffRoles[staffId] = roleId;
  }
  return { roles: symbols?.roles ?? [], staffRoles };
}

/** CSS classes of a symbol, for [ngClass] next to "fa-fw" */
export function roleSymbolClasses(symbol: RoleSymbol): string {
  return [symbol.solid ? 'fa-solid' : 'fa-light', symbol.icon, symbol.colorClass].filter(c => c).join(' ');
}

/** What a staff icon needs: the full staff list has identityUsername and locked, the names list hasIdentity */
export interface StaffIconSource {
  id: string;
  usingLevel?: number;
  locked?: boolean;
  identityUsername?: string;
  hasIdentity?: boolean;
}

/**
 * Staff icon: the role symbol, with a lock for locked accounts and a shield for login identities. The
 * shield replaces only the default user icon, so a custom role's icon stays visible. Staff Management,
 * the alarm route recipients and the receiver teams all use this (the names list has no lock state).
 */
export function staffListIconClasses(staff: StaffIconSource, symbols: IRoleSymbols | null | undefined): string {
  const symbol = staffRoleSymbol(staff.id, staff.usingLevel, symbols);
  if (staff.locked) {
    symbol.icon = 'fa-lock-alt';
  } else if ((staff.identityUsername || staff.hasIdentity) && symbol.icon === DEFAULT_ROLE_ICON) {
    symbol.icon = 'fa-user-shield';
  }
  return roleSymbolClasses(symbol);
}

/**
 * Icon of an alarm route recipient: the staff icon of a staff member, the team icon of a receiver team.
 * A staff member that isn't in the staff list (e.g. outside the user's location) gets the plain user icon.
 */
export function recipientIconClasses(recipient: { id: string; type: RecipientType | string },
                                     staffById: ReadonlyMap<string, StaffIconSource>,
                                     symbols: IRoleSymbols | null | undefined): string {
  if (recipient?.type !== RecipientType.User) {
    return 'fa-light fa-users fa-fw';
  }
  const staff = staffById.get(recipient.id);
  return `${staff ? staffListIconClasses(staff, symbols) : roleSymbolClasses(customRoleSymbol(null))} fa-fw`;
}

/** Staff by ID, for looking up recipients' and team members' roles and login identities */
export function staffById<T extends StaffIconSource>(staff: T[] | null | undefined): Map<string, T> {
  return new Map((staff ?? []).map(s => [s.id, s]));
}

/** One entry of the role legend */
export interface RoleLegendEntry {
  key: string;
  classes: string;
  /** Translation key of a built-in role */
  nameKey?: string;
  /** Name of a custom role */
  name?: string;
  testId?: string;
}

/**
 * Role legend: the built-in roles the customer has (feature roles only with the feature, EMAAC accounts
 * only for Everon admins, who alone see them), then the customer's custom roles. Same built-in roles
 * and order as the Staff Management legend has always had.
 */
export function roleLegend(featureEnabled: (feature: string) => boolean, userLevel: number | undefined,
                           symbols: IRoleSymbols | null | undefined): RoleLegendEntry[] {
  const builtIn: { level: number; nameKey: string; visible: boolean; testId?: string }[] = [
    { level: UserRoleLevel.CustomerAdmin, nameKey: 'STAFF_CUSTOMER_ADMIN', visible: true },
    { level: UserRoleLevel.MainUser, nameKey: 'STAFF_MAIN_USER', visible: true },
    { level: UserRoleLevel.Editor, nameKey: 'STAFF_USER', visible: true },
    { level: UserRoleLevel.ReadOnly, nameKey: 'STAFF_READONLY', visible: true },
    { level: UserRoleLevel.EMAOnly, nameKey: 'EveronMobileApp', visible: featureEnabled('EveronMobileApp') },
    { level: UserRoleLevel.AnnaPerennaMainUser, nameKey: 'STAFF_ONON_MAIN_USER', visible: featureEnabled('AnnaPerenna') || featureEnabled('OnOn') },
    { level: UserRoleLevel.EmaacOperator, nameKey: 'STAFF_EMAAC_OPERATOR', visible: featureEnabled('EMAAC'), testId: 'legend-emaac-operator' },
    // EMAAC accounts live in an Everon-managed company: only Everon admins see them
    { level: UserRoleLevel.ControlRoomUser, nameKey: 'STAFF_EMAAC_ACCOUNT', visible: userLevel === UserRoleLevel.SystemAdmin, testId: 'legend-emaac-account' },
  ];

  const builtInEntries: RoleLegendEntry[] = builtIn.filter(r => r.visible).map(r => ({
    key: `builtIn:${r.level}`,
    classes: roleSymbolClasses(builtInRoleSymbol(r.level)),
    nameKey: r.nameKey,
    testId: r.testId ?? `legend-role-${r.level}`,
  }));

  const customEntries: RoleLegendEntry[] = [...(symbols?.roles ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(r => ({
      key: `custom:${r.id}`,
      classes: roleSymbolClasses(customRoleSymbol(r.icon)),
      name: r.name,
      testId: `legend-custom-role-${r.id}`,
    }));

  return [...builtInEntries, ...customEntries];
}

/**
 * Legend entries for the account states the staff icons show besides the role: the shield of staff who
 * sign in with a login identity. Shown when the listed staff have one, whatever the customer's features:
 * identities are also linked without strong authentication. Not a role, so not a role filter option.
 */
export function accountStateLegend(showIdentity: boolean): RoleLegendEntry[] {
  return showIdentity
    ? [{ key: 'state:identity', classes: 'fa-light fa-user-shield', nameKey: 'STRONG_AUTHENTICATION_USER', testId: 'legend-identity' }]
    : [];
}

/** Does anyone in the list sign in with a login identity (shows the shield in the legend) */
export function anyHasIdentity(staff: StaffIconSource[] | null | undefined): boolean {
  return (staff ?? []).some(s => !!(s.identityUsername || s.hasIdentity));
}

/** Customer features from the login, as the components read them */
export function storedFeatureEnabled(feature: string): boolean {
  try {
    const features = JSON.parse(localStorage.getItem('features'));
    return Array.isArray(features) && features.includes(feature);
  } catch {
    return false;
  }
}
